import { BaseDb } from "common/BaseDb"
import { HttpError, isHttpError } from "common/http"
import { HubSchemaWithId, User } from "components/hub/hub.schema"

/**
 * Reactivates a removed hub user in place. Roles are reset to collaborator so a former hub admin re-invited to a
 * single project doesn't silently regain admin rights over the whole hub.
 *
 * @param hubUser The inactive hub user; mutated in place.
 * @param invitedBy The user who re-invited them.
 * @param sendInviteEmail Whether an invite email should be sent for this reactivation.
 */
export function reactivateHubUser(hubUser: User, invitedBy: number, sendInviteEmail: boolean): void {
    hubUser.active = true
    hubUser.roles = ["collaborator"]
    hubUser.metadata = { invite_sent: !sendInviteEmail, invited_by: invitedBy }
}

export class HubDb extends BaseDb<HubSchemaWithId> {
    /**
     * Adds user to a hub
     *
     * @param hubId The hub id.
     * @param userId The user id.
     * @param sendInviteEmail Send invite email yes or no.
     * @param invitedBy The user who invited this person.
     * @param [sessionId] The optional session id.
     * @returns user to hub
     */
    public async addUserToHub(
        hubId: number,
        userId: number,
        sendInviteEmail: boolean,
        invitedBy: number,
        sessionId?: string
    ): Promise<HubSchemaWithId[] | HttpError> {
        const hubResponse = await this.read(hubId, sessionId)
        if (isHttpError(hubResponse)) return hubResponse

        const hub = hubResponse[0]

        const existingUser = hub.data.users.find((user) => user.id === userId)
        if (existingUser?.active) return [hub]

        // NOTE A previously removed hub user must be reactivated when re-invited, otherwise
        // propagateHubUserChanges keeps deactivating them in every hub project on each hub save.
        if (existingUser) {
            reactivateHubUser(existingUser, invitedBy, sendInviteEmail)

            return this.update(hub, sessionId)
        }

        // TODO hook this up to the companies config.json? YES!
        const roles = hub.data.users.length === 0 ? ["admin"] : ["collaborator"]

        hub.data.users.push({
            id: userId,
            roles,
            active: true,
            metadata: {
                invited_by: invitedBy,
                invite_sent: !sendInviteEmail
            }
        })

        return this.update(hub, sessionId)
    }

    /**
     * removeUserFromHub
     *
     * @param hubId The hub id.
     * @param userId The user id.
     * @param [sessionId] the optional session id.
     * @returns HubSchema[]
     */
    public async removeUserFromHub(hubId: number, userId: number, sessionId?: string): Promise<HubSchemaWithId[] | HttpError> {
        const read = await this.read(hubId, sessionId)
        if (isHttpError(read)) return read
        read[0].data.users = read[0].data.users.filter((user) => {
            if (user.id !== userId) return user
        })

        const updated = await this.update(read[0], sessionId)
        if (isHttpError(updated)) return updated
        return updated
    }
}

export default new HubDb("hub")
